'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Snapshot {
  id: string;
  nome: string;
  descrizione: string;
  snapshot_data: any;
  created_at: string;
}

export default function TopologySnapshotsContent() {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedA, setSelectedA] = useState<Snapshot | null>(null);
  const [selectedB, setSelectedB] = useState<Snapshot | null>(null);
  const [comparing, setComparing] = useState(false);
  const [diffResult, setDiffResult] = useState<any>(null);

  const fetchSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('topology_snapshots').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setSnapshots(data || []);
    } catch (err: any) {
      toast.error('Errore caricamento snapshot: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSnapshots(); }, [fetchSnapshots]);

  const handleDelete = async (id: string, nome: string) => {
    if (!confirm(`Eliminare lo snapshot "${nome}"?`)) return;
    try {
      const { error } = await supabase.from('topology_snapshots').delete().eq('id', id);
      if (error) throw error;
      toast.success('Snapshot eliminato');
      fetchSnapshots();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const handleCompare = () => {
    if (!selectedA || !selectedB) { toast.error('Seleziona due snapshot da confrontare'); return; }
    setComparing(true);
    const dataA = selectedA.snapshot_data;
    const dataB = selectedB.snapshot_data;
    const nodesA = (dataA.nodes || []).map((n: any) => n.id);
    const nodesB = (dataB.nodes || []).map((n: any) => n.id);
    const added = nodesB.filter((id: string) => !nodesA.includes(id));
    const removed = nodesA.filter((id: string) => !nodesB.includes(id));
    const common = nodesA.filter((id: string) => nodesB.includes(id));
    const changed = common.filter((id: string) => {
      const nA = (dataA.nodes || []).find((n: any) => n.id === id);
      const nB = (dataB.nodes || []).find((n: any) => n.id === id);
      return nA && nB && (nA.status !== nB.status || nA.ip !== nB.ip);
    }).map((id: string) => {
      const nA = (dataA.nodes || []).find((n: any) => n.id === id);
      const nB = (dataB.nodes || []).find((n: any) => n.id === id);
      return { id, labelA: nA?.label, labelB: nB?.label, statusA: nA?.status, statusB: nB?.status, ipA: nA?.ip, ipB: nB?.ip };
    });
    setDiffResult({ added, removed, changed, nodesCountA: nodesA.length, nodesCountB: nodesB.length, linksCountA: dataA.links_count || 0, linksCountB: dataB.links_count || 0 });
    setComparing(false);
  };

  return (
    <div className="flex flex-col h-full bg-background overflow-auto scrollbar-thin">
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Storico Topologia</h1>
            <p className="text-sm text-secondary-color mt-0.5">Snapshot topologia rete con confronto visuale</p>
          </div>
          <button onClick={fetchSnapshots} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded-lg text-sm font-semibold hover:bg-sky-500/20 transition-all">
            <Icon name="ArrowPathIcon" size={15} />
            Aggiorna
          </button>
        </div>
      </div>

      <div className="flex-1 p-6 space-y-6">
        {/* Snapshot list */}
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="text-base font-bold text-white">Snapshot Disponibili ({snapshots.length})</h2>
            <p className="text-xs text-muted-color">Seleziona due snapshot per confrontarli</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : snapshots.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <Icon name="CameraIcon" size={32} className="text-muted-color" />
              <p className="text-secondary-color font-semibold text-sm">Nessuno snapshot. Usa la Mappa di Rete per crearne uno.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {snapshots.map(s => (
                <div key={s.id} className="flex items-center gap-4 px-5 py-4 hover:bg-surface-elevated/30 transition-colors">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedA(selectedA?.id === s.id ? null : s)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${selectedA?.id === s.id ? 'border-sky-500 bg-sky-500/20 text-sky-400' : 'border-border text-muted-color hover:border-sky-500/50'}`}
                      title="Seleziona come A"
                    >A</button>
                    <button
                      onClick={() => setSelectedB(selectedB?.id === s.id ? null : s)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${selectedB?.id === s.id ? 'border-violet-500 bg-violet-500/20 text-violet-400' : 'border-border text-muted-color hover:border-violet-500/50'}`}
                      title="Seleziona come B"
                    >B</button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{s.nome}</p>
                    <p className="text-xs text-muted-color">{s.descrizione}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-color">
                    <span>{s.snapshot_data?.nodes_count || 0} nodi</span>
                    <span>{s.snapshot_data?.links_count || 0} link</span>
                    <span>{new Date(s.created_at).toLocaleString('it-IT')}</span>
                  </div>
                  <button onClick={() => handleDelete(s.id, s.nome)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors">
                    <Icon name="TrashIcon" size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Compare button */}
        {selectedA && selectedB && (
          <div className="flex items-center gap-4 p-4 bg-surface border border-border rounded-xl">
            <div className="flex-1">
              <p className="text-sm text-secondary-color">
                Confronto: <span className="text-sky-400 font-semibold">{selectedA.nome}</span> (A) vs <span className="text-violet-400 font-semibold">{selectedB.nome}</span> (B)
              </p>
            </div>
            <button onClick={handleCompare} disabled={comparing} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all disabled:opacity-50">
              <Icon name="ArrowsRightLeftIcon" size={15} />
              {comparing ? 'Confronto...' : 'Confronta'}
            </button>
          </div>
        )}

        {/* Diff result */}
        {diffResult && (
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="text-base font-bold text-white">Risultato Confronto</h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Nodi A', value: diffResult.nodesCountA, color: 'text-sky-400' },
                  { label: 'Nodi B', value: diffResult.nodesCountB, color: 'text-violet-400' },
                  { label: 'Aggiunti', value: diffResult.added.length, color: 'text-green-400' },
                  { label: 'Rimossi', value: diffResult.removed.length, color: 'text-red-400' },
                ].map(k => (
                  <div key={k.label} className="bg-surface-elevated rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-color font-semibold uppercase tracking-wide">{k.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
                  </div>
                ))}
              </div>

              {diffResult.added.length > 0 && (
                <div>
                  <p className="text-sm font-bold text-green-400 mb-2">✚ Nodi Aggiunti ({diffResult.added.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {diffResult.added.map((id: string) => (
                      <span key={id} className="px-2 py-1 bg-green-500/10 border border-green-500/20 rounded text-xs font-mono text-green-400">{id}</span>
                    ))}
                  </div>
                </div>
              )}

              {diffResult.removed.length > 0 && (
                <div>
                  <p className="text-sm font-bold text-red-400 mb-2">✕ Nodi Rimossi ({diffResult.removed.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {diffResult.removed.map((id: string) => (
                      <span key={id} className="px-2 py-1 bg-red-500/10 border border-red-500/20 rounded text-xs font-mono text-red-400">{id}</span>
                    ))}
                  </div>
                </div>
              )}

              {diffResult.changed.length > 0 && (
                <div>
                  <p className="text-sm font-bold text-amber-400 mb-2">⚡ Nodi Modificati ({diffResult.changed.length})</p>
                  <div className="space-y-2">
                    {diffResult.changed.map((c: any) => (
                      <div key={c.id} className="flex items-center gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                        <span className="font-mono text-xs text-white">{c.labelA || c.id}</span>
                        {c.statusA !== c.statusB && (
                          <span className="text-xs text-muted-color">stato: <span className="text-red-400">{c.statusA}</span> → <span className="text-green-400">{c.statusB}</span></span>
                        )}
                        {c.ipA !== c.ipB && (
                          <span className="text-xs text-muted-color">IP: <span className="text-red-400 font-mono">{c.ipA}</span> → <span className="text-green-400 font-mono">{c.ipB}</span></span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {diffResult.added.length === 0 && diffResult.removed.length === 0 && diffResult.changed.length === 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <Icon name="CheckCircleIcon" size={16} className="text-green-400" />
                  <p className="text-sm text-green-400 font-semibold">Nessuna differenza rilevata tra i due snapshot</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
